export const M6_UNIT_10 = {
  id: "m6-u10-food-restaurants",
  title: "Unidad 10: Food & Restaurants",
  topic: "Master dining out! Order food like a native, understand menus, handle dietary needs, and navigate restaurant culture. Essential for food lovers!",
  iconType: "utensils",
  grammarImage: "/grammar/grammar-m6-u10.jpg",
  grammarText: "WOULD LIKE for polite ordering. CAN/COULD for requests. Present Perfect for food experiences. Learn restaurant vocabulary and cultural dining etiquette!",
  sets: [

    // ===================================================================
    // SET 1: RESTAURANT VOCABULARY FOUNDATION (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s1",
      title: "Set 1: Restaurant Basics",
      questions: [
        {
          type: "match",
          q: "Match restaurant vocabulary",
          pairs: [
            { left: "Appetizer", right: "Entrante/entrada 🥗" },
            { left: "Entrée", right: "Plato principal 🍝" },
            { left: "Dessert", right: "Postre 🍰" }
          ],
          a: "completed",
          successtip: "✓MENU ORDER!\nAppetizer → Entrée → Dessert\n🇺🇸 'Entrée' = main course\n🇫🇷 En Francia = starter (diferente!)"
        },
        {
          type: "mc",
          q: "Before eating, you look at the ___.",
          options: ["menu", "bill", "tip"],
          a: "menu",
          tip: "MENU = carta/menú",
          successtip: "✓MENU!\nMenu = carta 📋\nUS: huge menus\nEurope: smaller, seasonal"
        },
        {
          type: "order",
          q: "Context: Pidiendo ver el menú. Traduce: ¿Puedo ver el menú, por favor?",
          words: ["Can", "I", "see", "the", "menu,", "please?"],
          a: "Can I see the menu, please?",
          tip: "CAN I = casual request (ya conoces!)"
        },
        {
          type: "input",
          q: "The person who serves you food is the ___ or server.",
          a: "waiter",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["waitress", "server"] },
          successtip: "✓SERVER!\n🇺🇸 'Server' = gender neutral (modern)\nWaiter/waitress = traditional\nTip them well! 💸"
        },
        {
          type: "mc",
          q: "Eating AT the restaurant (not takeout):",
          options: ["dine in", "take out", "order in"],
          a: "dine in",
          tip: "DINE IN vs TAKE OUT vs ORDER IN",
          successtip: "✓THREE OPTIONS!\nDine in = comer ahí 🍽️\nTake out = para llevar 🥡\nOrder in = delivery 🚗"
        },
        {
          type: "order",
          q: "Context: Haciendo reservación. Traduce: Me gustaría hacer una reservación.",
          words: ["I'd", "like", "to", "make", "a", "reservation."],
          a: "I'd like to make a reservation.",
          tip: "I'D LIKE = I would like (polite)",
          successtip: "✓I'D LIKE!\nI'd like = formal/polite 🎩\nI want = direct/casual 👕\nRestaurants = use polite!"
        },
        {
          type: "mc",
          q: "Private seating area (not regular table):",
          options: ["booth", "counter", "bar"],
          a: "booth",
          successtip: "✓BOOTH!\nBooth = cabina/mesa privada 🛋️\nMore private, comfy\nDiners clásicos tienen booths"
        },
        {
          type: "input",
          q: "At the end, you ask for the ___ (US) or bill (UK).",
          a: "check",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["bill"] },
          successtip: "✓CHECK vs BILL!\n🇺🇸 'Check, please!'\n🇬🇧 'Bill, please!'\nAmbas = la cuenta 💳"
        },
        {
          type: "match",
          q: "Match meal times",
          pairs: [
            { left: "Breakfast", right: "Morning meal ☕" },
            { left: "Lunch", right: "Midday meal 🥪" },
            { left: "Dinner", right: "Evening meal 🍽️" }
          ],
          a: "completed",
          successtip: "✓BRUNCH!\nBreakfast + Lunch = Brunch 🥞\nWeekend tradition en US\n10AM-2PM typically"
        },
        {
          type: "mc",
          q: "Food you take home after eating:",
          options: ["leftovers / doggy bag", "new food", "extra order"],
          a: "leftovers / doggy bag",
          successtip: "✓DOGGY BAG!\nLeftovers = sobras 🥡\nDoggy bag = para llevar sobras\nAmerican portions = HUGE, always leftovers 😂"
        }
      ]
    },

    // ===================================================================
    // SET 2: ORDERING - MODAL MASTERY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s2",
      title: "Set 2: How to Order",
      questions: [
        {
          type: "match",
          q: "Match ordering phrases by politeness",
          pairs: [
            { left: "Most casual", right: "I want the burger" },
            { left: "Polite", right: "Can I have the burger?" },
            { left: "Most formal", right: "I'd like the burger" }
          ],
          a: "completed",
          successtip: "✓POLITENESS LEVELS!\nI want = OK con amigos\nCan I = standard\nI'd like = best para restaurants 🎯"
        },
        {
          type: "mc",
          q: "Most polite way to order: '___  the pasta, please.'",
          options: ["I'd like", "I want", "Give me"],
          a: "I'd like",
          tip: "I'D LIKE = would like (most polite)",
          successtip: "✓I'D LIKE!\nI'd like = ordering standard 💯\nServers expect this\nNever 'Give me' ❌ (rude!)"
        },
        {
          type: "order",
          q: "Context: Pidiendo agua cortésmente. Traduce: ¿Podría traerme agua, por favor?",
          words: ["Could", "I", "get", "some", "water,", "please?"],
          a: "Could I get some water, please?",
          tip: "COULD I GET = very polite request",
          successtip: "✓COULD I!\nCould I = más formal que Can I 🎩\nPerfect para waiters\nAlways add 'please' 💯"
        },
        {
          type: "input",
          q: "Waiter asks: 'What ___ you like?' (ordering)",
          a: "would",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓WAITER PHRASE!\n'What would you like?' = standard waiter question 🍽️\nYour cue to order!"
        },
        {
          type: "mc",
          q: "Adding something to your order: 'Can I ___ have fries?'",
          options: ["also", "too", "more"],
          a: "also",
          tip: "ALSO = adding to order",
          successtip: "✓ALSO!\nCan I also have... = añadir algo 🍟\nBuilding your order\n'And' works too!"
        },
        {
          type: "order",
          q: "Context: Ordenando para alguien más. Traduce: Ella tomará el salmón.",
          words: ["She'll", "have", "the", "salmon."],
          a: "She'll have the salmon.",
          tip: "SHE'LL HAVE = ordering for someone",
          successtip: "✓HAVE!\n'Have' = ordering verb 📝\n'I'll have X' = tomaré X\nNot 'take' or 'get' aquí"
        },
        {
          type: "mc",
          q: "Choosing between options: 'I'll have the chicken ___ the beef.'",
          options: ["instead of", "and", "with"],
          a: "instead of",
          successtip: "✓INSTEAD OF!\nInstead of = en lugar de 🔄\nChanging your mind\n'Actually, I'll have...' también"
        },
        {
          type: "input",
          q: "Waiter: 'Anything to ___?' (beverages)",
          a: "drink",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓DRINK!\n'Anything to drink?' = waiter question clásica 🥤\nComes before or after food order"
        },
        {
          type: "mc",
          q: "You're not ready to order yet: '___'",
          options: ["Can I have a few more minutes?", "I'm ready now!", "Bring me food!"],
          a: "Can I have a few more minutes?",
          successtip: "✓NOT READY!\n'Few more minutes' = todavía no listo ⏰\nWaiters understand\nDon't stress! 😅"
        },
        {
          type: "match",
          q: "Match ordering situations",
          pairs: [
            { left: "First order", right: "I'd like the steak" },
            { left: "Adding more", right: "Can I also have..." },
            { left: "For someone else", right: "He'll have the fish" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 3: MENU NAVIGATION (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s3",
      title: "Set 3: Understanding Menus",
      questions: [
        {
          type: "match",
          q: "Match menu sections",
          pairs: [
            { left: "Starters", right: "Appetizers 🥗" },
            { left: "Mains", right: "Entrées/Main courses 🍖" },
            { left: "Sides", right: "Side dishes 🍟" }
          ],
          a: "completed",
          successtip: "✓MENU STRUCTURE!\nStarters/Apps → Mains → Sides → Desserts\nSome menus have 'Specials' = daily recommendations 💫"
        },
        {
          type: "mc",
          q: "Small dish before main course:",
          options: ["appetizer / starter", "dessert", "entrée"],
          a: "appetizer / starter",
          tip: "APPETIZER = starter",
          successtip: "✓APPETIZER!\n🇺🇸 Appetizer / Apps\n🇬🇧 Starter\nSame thing! 🥗\nShared often"
        },
        {
          type: "order",
          q: "Context: Preguntando por recomendación. Traduce: ¿Cuál es la especialidad de la casa?",
          words: ["What's", "the", "house", "specialty?"],
          a: "What's the house specialty?",
          tip: "HOUSE SPECIALTY = specialty of the house",
          successtip: "✓SPECIALTY!\nHouse specialty = especialidad 👨‍🍳\n'What do you recommend?' = también OK\nChef's favorite!"
        },
        {
          type: "input",
          q: "Food that comes WITH your main dish: ___ dish",
          a: "side",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓SIDE DISH!\nSide = acompañamiento 🍟\nFries, veggies, rice, salad...\nOften included or extra cost"
        },
        {
          type: "mc",
          q: "Menu says 'served with...' This means:",
          options: ["Comes with (included)", "Costs extra", "Not available"],
          a: "Comes with (included)",
          successtip: "✓SERVED WITH!\n'Served with' = viene con ✓\nIncluded in price\n'Add' or 'Extra' = costs more 💰"
        },
        {
          type: "order",
          q: "Context: Preguntando por ingredientes. Traduce: ¿Qué lleva esto?",
          words: ["What's", "in", "this?"],
          a: "What's in this?",
          tip: "WHAT'S IN = what ingredients",
          successtip: "✓INGREDIENTS!\n'What's in this?' = qué tiene 🤔\nImportant para allergies\nOr curiosity!"
        },
        {
          type: "mc",
          q: "Menu description: 'Topped with cheese' means:",
          options: ["Cheese on top", "Cheese inside", "Cheese on the side"],
          a: "Cheese on top",
          successtip: "✓TOPPED WITH!\nTopped with = encima de 🧀\nMenu vocab común\n'Stuffed with' = relleno de\n'Drizzled with' = rociado de"
        },
        {
          type: "input",
          q: "Asking about portion size: 'How ___ is the portion?'",
          a: "big",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["large"] },
          successtip: "✓PORTIONS!\nAmerican portions = ENORME 😅\nOften enough for 2\nAsk before ordering!"
        },
        {
          type: "mc",
          q: "Between two sizes: 'I'll have the ___ size.' (not small, not large)",
          options: ["medium / regular", "middle", "normal"],
          a: "medium / regular",
          successtip: "✓SIZES!\nSmall / Medium (Regular) / Large 📏\nFast food: Small/Medium/Large/XL\nCoffee: Tall/Grande/Venti (Starbucks invented own! ☕)"
        },
        {
          type: "match",
          q: "Match menu terms to meanings",
          pairs: [
            { left: "Market price", right: "Price varies (usually expensive)" },
            { left: "Daily special", right: "Today's recommendation" },
            { left: "Chef's choice", right: "Chef decides" }
          ],
          a: "completed",
          successtip: "✓MARKET PRICE!\n'Market price' = ask first 💰\nUsually seafood/expensive items\nCan be surprise bill shock! 😱"
        }
      ]
    },

    // ===================================================================
    // SET 4: DIETARY RESTRICTIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s4",
      title: "Set 4: Dietary Needs",
      questions: [
        {
          type: "match",
          q: "Match dietary terms",
          pairs: [
            { left: "Vegetarian", right: "No meat 🥬" },
            { left: "Vegan", right: "No animal products 🌱" },
            { left: "Gluten-free", right: "No wheat/gluten 🚫🌾" }
          ],
          a: "completed",
          successtip: "✓DIETS!\nVegetarian = no meat\nVegan = no animal products (milk, eggs, honey)\nPescatarian = fish OK, no meat 🐟"
        },
        {
          type: "mc",
          q: "'I'm ___ to nuts.' (health reaction)",
          options: ["allergic", "allergy", "allergen"],
          a: "allergic",
          tip: "ALLERGIC TO = alérgico a",
          successtip: "✓ALLERGIC!\nAllergic to = alérgico a 🚫\nSerious! Always tell waiter\nEpiPen = emergency device 💉"
        },
        {
          type: "order",
          q: "Context: Preguntando por ingredientes alérgicos. Traduce: ¿Esto contiene nueces?",
          words: ["Does", "this", "contain", "nuts?"],
          a: "Does this contain nuts?",
          tip: "CONTAIN = contener",
          successtip: "✓CONTAIN!\nContain = contener 📋\nNuts = nueces\nPeanuts = cacahuates (different allergy!)"
        },
        {
          type: "input",
          q: "'Can you make it ___ dairy?' (no milk/cheese)",
          a: "without",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓WITHOUT!\n'Without' = sin 🚫\nDairy = lácteos 🥛\nLactose intolerant = common issue"
        },
        {
          type: "mc",
          q: "No animal products at all:",
          options: ["vegan", "vegetarian", "pescatarian"],
          a: "vegan",
          successtip: "✓VEGAN!\nVegan = 100% plant-based 🌱\nNo meat, milk, eggs, honey, leather...\nUS restaurants = usually have options now"
        },
        {
          type: "order",
          q: "Context: Informando restricción. Traduce: Soy vegetariano.",
          words: ["I'm", "vegetarian."],
          a: "I'm vegetarian.",
          tip: "I'M + dietary term",
          successtip: "✓VEGETARIAN!\nVegetarian = vegetariano 🥗\nMost restaurants accommodate\nLondon/SF/NYC = tons of options"
        },
        {
          type: "mc",
          q: "No wheat products:",
          options: ["gluten-free", "dairy-free", "sugar-free"],
          a: "gluten-free",
          successtip: "✓GLUTEN-FREE!\nGluten-free = sin gluten 🚫🌾\nTrending en US\nCeliac disease = serious condition\nMany restaurants have GF menus now"
        },
        {
          type: "input",
          q: "'Is this dish ___?' (asking if vegetarian)",
          a: "vegetarian",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "IS THIS + dietary term?"
        },
        {
          type: "mc",
          q: "Can't have sugar:",
          options: ["sugar-free / diabetic", "salt-free", "fat-free"],
          a: "sugar-free / diabetic",
          successtip: "✓SUGAR-FREE!\nDiabetic = diabético 💉\nSugar-free options common\nArtificial sweeteners = substitutes"
        },
        {
          type: "match",
          q: "Match allergy phrases",
          pairs: [
            { left: "Serious allergy", right: "I'm allergic to..." },
            { left: "Preference", right: "I don't eat..." },
            { left: "Checking", right: "Does this contain...?" }
          ],
          a: "completed",
          successtip: "✓ALLERGIC vs PREFERENCE!\nAllergic = health risk ⚠️\nDon't eat = choice 🤷\nRestaurants treat allergies seriously!"
        }
      ]
    },

    // ===================================================================
    // SET 5: RESTAURANT EXPERIENCE - FULL DIALOGUE (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s5",
      title: "Set 5: Your Restaurant Visit",
      questions: [
        {
          type: "mc",
          q: "HOST: 'Welcome! Do you have a reservation?' YOU: '___'",
          options: ["Yes, under Smith for 7 PM", "No reservation needed", "Give me table now"],
          a: "Yes, under Smith for 7 PM",
          tip: "'UNDER' = a nombre de",
          successtip: "✓UNDER!\n'Under [name]' = a nombre de 📋\nReservation phrase standard\n'Table for 2' si no hay reservation"
        },
        {
          type: "order",
          q: "HOST: 'Right this way.' (leads you) WAITER arrives: Traduce tu saludo: Hola, ¿cómo estás?",
          words: ["Hi,", "how", "are", "you?"],
          a: "Hi, how are you?",
          tip: "Be polite to waiters!",
          successtip: "✓BE NICE!\nPolite to servers = better service 😊\nThey remember nice customers\nTip them well! 💰"
        },
        {
          type: "input",
          q: "WAITER: 'Can I start you off with something to drink?' YOU: 'I'd ___ water, please.'",
          a: "like",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓START YOU OFF!\n'Start you off with' = comenzar con 🥤\nWaiter phrase común\nDrinks come first!"
        },
        {
          type: "mc",
          q: "WAITER: 'Are you ready to order?' YOU: (still deciding) '___'",
          options: ["Can I have a few more minutes?", "I'm ready now", "Bring food immediately"],
          a: "Can I have a few more minutes?",
          tip: "It's OK to need time!",
          successtip: "✓NO RUSH!\n'Few more minutes' = totally fine ⏰\nWaiter will come back\nDon't stress menu! 😌"
        },
        {
          type: "order",
          q: "Ready now! WAITER returns. Traduce: Me gustaría la hamburguesa, por favor.",
          words: ["I'd", "like", "the", "burger,", "please."],
          a: "I'd like the burger, please.",
          tip: "I'D LIKE = ordering standard"
        },
        {
          type: "mc",
          q: "WAITER: 'How would you like that cooked?' (burger) YOU: '___'",
          options: ["Medium, please", "Hot, please", "Fast, please"],
          a: "Medium, please",
          tip: "Meat cooking levels!",
          successtip: "✓DONENESS!\n🥩 Rare = muy rojo\n🥩 Medium-rare = poco cocido (best!)\n🥩 Medium = término medio\n🥩 Well-done = bien cocido\nUS loves medium-rare 💯"
        },
        {
          type: "input",
          q: "WAITER: 'Any sides with that?' YOU: 'Can I get ___, please?' (fried potatoes)",
          a: "fries",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓FRIES!\n🇺🇸 Fries / French fries\n🇬🇧 Chips\nDifferent words! 🍟\nSweet potato fries = trending"
        },
        {
          type: "mc",
          q: "WAITER checks mid-meal: 'How is everything?' YOU: '___'",
          options: ["Everything's great, thanks!", "I hate this", "More food now"],
          a: "Everything's great, thanks!",
          successtip: "✓CHECK-IN!\n'How is everything?' = waiter check 👍\nStandard response: 'Great!'\nComplain only if real problem\nThis is a 🪤 trap = yes if not great"
        },
        {
          type: "order",
          q: "Finished eating! Traduce: ¿Puedo pedir la cuenta, por favor?",
          words: ["Can", "I", "get", "the", "check,", "please?"],
          a: "Can I get the check, please?",
          tip: "CHECK = cuenta (US)",
          successtip: "✓CHECK!\n🇺🇸 'Check, please!'\n🇬🇧 'Bill, please!'\nWave hand subtly\nOr say to passing waiter"
        },
        {
          type: "mc",
          q: "WAITER brings check. Bill is $40. Good tip amount (US):",
          options: ["$8 (20%)", "$2 (5%)", "$0 (no tip)"],
          a: "$8 (20%)",
          tip: "US tipping culture!",
          successtip: "✓TIPPING!\n🇺🇸 US: 15-20% MANDATORY 💸\nWaiters earn $2-3/hr + tips\n20% = good service\n15% = OK service\nNo tip = insulting ❌\n\nCalculator: Bill × 0.20 = tip"
        }
      ]
    },

    // ===================================================================
    // SET 6: COOKING METHODS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s6",
      title: "Set 6: Cooking Verbs",
      questions: [
        {
          type: "match",
          q: "Match cooking methods",
          pairs: [
            { left: "Grill", right: "A la parrilla 🔥" },
            { left: "Bake", right: "Hornear 🥧" },
            { left: "Fry", right: "Freír 🍳" }
          ],
          a: "completed",
          successtip: "✓COOKING VERBS!\nThese appear on EVERY menu 📋\nDescribe preparation method"
        },
        {
          type: "mc",
          q: "Menu: 'Grilled chicken' means:",
          options: ["Cooked on grill with fire/heat", "Cooked in oven", "Cooked in water"],
          a: "Cooked on grill with fire/heat",
          tip: "GRILL = parrilla",
          successtip: "✓GRILLED!\nGrilled = a la parrilla 🔥\nHealthier option\nBBQ = type of grilling\nOutdoor American tradition"
        },
        {
          type: "order",
          q: "Context: Receta simple. Traduce: Hornea por 20 minutos.",
          words: ["Bake", "for", "20", "minutes."],
          a: "Bake for 20 minutes.",
          tip: "BAKE = hornear (oven)",
          successtip: "✓BAKE!\nBake = horno 🥧\nBread, cakes, cookies\nBake vs roast = similar (roast = usually meat)"
        },
        {
          type: "input",
          q: "Cooking in hot oil: ___ (verb)",
          a: "fry",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓FRY!\nFry = freír 🍳\nDeep-fry = sumergido en aceite\nPan-fry = sartén con poco aceite\nStir-fry = saltear (Asian style)"
        },
        {
          type: "mc",
          q: "Menu: 'Steamed vegetables' - cooked how?",
          options: ["With steam/hot vapor", "In oven", "On grill"],
          a: "With steam/hot vapor",
          successtip: "✓STEAMED!\nSteam = vapor 💨\nHealthy cooking!\nNo oil needed\nAsian cuisine usa mucho"
        },
        {
          type: "order",
          q: "Context: Receta - vegetales. Traduce: Corta las zanahorias.",
          words: ["Cut", "the", "carrots."],
          a: "Cut the carrots.",
          tip: "Imperatives for recipes!",
          successtip: "✓CUT!\nCut = cortar 🔪\nChop = picar\nDice = cubos pequeños\nSlice = rebanadas\nMince = muy picado"
        },
        {
          type: "mc",
          q: "Cooking in boiling water:",
          options: ["boil", "grill", "bake"],
          a: "boil",
          successtip: "✓BOIL!\nBoil = hervir 💧\nPasta, eggs, vegetables\nBoiling water = 100°C / 212°F\nSimmer = gentle boiling"
        },
        {
          type: "input",
          q: "Menu: '___  salmon' (cooked in oven with heat from above)",
          a: "roasted",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["roast"] },
          successtip: "✓ROAST!\nRoast = asar/hornear 🔥\nMeat, vegetables, chicken\nHigh heat, dry cooking\nRoast vs bake = roast usually meat/veggies"
        },
        {
          type: "mc",
          q: "Quick cooking in pan with little oil while stirring:",
          options: ["sauté / stir-fry", "boil", "steam"],
          a: "sauté / stir-fry",
          successtip: "✓SAUTÉ!\nSauté = saltear 🍳\nFrench term\nStir-fry = Asian version\nQuick, high heat, constant motion\nHealthy + tasty!"
        },
        {
          type: "match",
          q: "Match menu descriptions",
          pairs: [
            { left: "Fried chicken", right: "Cooked in oil" },
            { left: "Grilled steak", right: "Cooked on grill" },
            { left: "Baked potato", right: "Cooked in oven" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 7: TASTE & TEXTURE VOCABULARY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s7",
      title: "Set 7: Describing Food",
      questions: [
        {
          type: "match",
          q: "Match taste words",
          pairs: [
            { left: "Sweet", right: "Dulce 🍭" },
            { left: "Salty", right: "Salado 🧂" },
            { left: "Spicy", right: "Picante 🌶️" }
          ],
          a: "completed",
          successtip: "✓FIVE TASTES!\nSweet, Salty, Sour, Bitter, Umami (savory)\nUmami = Japanese discovery 🇯🇵\nMSG taste = umami"
        },
        {
          type: "mc",
          q: "Like candy or sugar:",
          options: ["sweet", "salty", "sour"],
          a: "sweet",
          tip: "SWEET = dulce",
          successtip: "✓SWEET!\nSweet = dulce 🍰\nDesserts, fruits, candy\nAmericans love sweet!\nEven bread is sweet in US 😅"
        },
        {
          type: "order",
          q: "Context: Describiendo comida deliciosa. Traduce: ¡Esto está delicioso!",
          words: ["This", "is", "delicious!"],
          a: "This is delicious!",
          tip: "DELICIOUS = compliment!",
          successtip: "✓DELICIOUS!\nDelicious = delicioso 😋\nBest compliment to chef/waiter\n'Tasty' también OK\n'Yummy' = infantil/casual"
        },
        {
          type: "input",
          q: "Hot pepper taste: ___",
          a: "spicy",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓SPICY!\nSpicy = picante 🌶️\nMild / Medium / Hot / Extra hot\nAmericans generally no aguantan mucho 😂\nMexican/Thai/Indian = spicy cuisines"
        },
        {
          type: "mc",
          q: "Like lemon or vinegar taste:",
          options: ["sour", "sweet", "bitter"],
          a: "sour",
          successtip: "✓SOUR!\nSour = ácido/agrio 🍋\nLemon, lime, vinegar\nSour candy = popular\nNot same as 'bitter' ❌"
        },
        {
          type: "mc",
          q: "Fried chicken texture on outside:",
          options: ["crispy / crunchy", "soft", "chewy"],
          a: "crispy / crunchy",
          tip: "CRISPY = crujiente",
          successtip: "✓CRISPY!\nCrispy = crujiente 🍗\nCrunchy = similar\nTexture words = important!\nChips, fried food, bacon..."
        },
        {
          type: "input",
          q: "Steak texture when cooked perfectly: ___",
          a: "tender",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["juicy"] },
          successtip: "✓TENDER!\nTender = suave/tierno 🥩\nJuicy = jugoso 💧\nBoth = good meat compliments\nOpposite = tough/dry"
        },
        {
          type: "order",
          q: "Context: Comida sin sabor. Traduce: Está un poco desabrido.",
          words: ["It's", "a", "bit", "bland."],
          a: "It's a bit bland.",
          tip: "BLAND = sin sabor (polite complaint)",
          successtip: "✓BLAND!\nBland = sin sabor 😐\nPolite way to say 'boring'\nNeeds salt/seasoning\nBetter than 'tastes bad' ❌"
        },
        {
          type: "mc",
          q: "Very rich/heavy food (lots of cream/butter):",
          options: ["rich", "light", "fresh"],
          a: "rich",
          successtip: "✓RICH!\nRich = pesado/con mucha grasa 🧈\nToo much = stomach ache\nOpposite = light\nAmerican food often rich 💰"
        },
        {
          type: "match",
          q: "Match texture descriptions",
          pairs: [
            { left: "Crispy bacon", right: "Crunchy texture" },
            { left: "Tender steak", right: "Soft texture" },
            { left: "Chewy bread", right: "Requires chewing" }
          ],
          a: "completed",
          successtip: "✓CHEWY!\nChewy = chicloso/masticable 🍞\nBagels, gummy candy, tough meat\nCan be good or bad! 🤷"
        }
      ]
    },

    // ===================================================================
    // SET 8: PROBLEMS & COMPLAINTS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s8",
      title: "Set 8: Handling Issues",
      questions: [
        {
          type: "match",
          q: "Match problem types",
          pairs: [
            { left: "Wrong order", right: "This isn't what I ordered" },
            { left: "Temperature", right: "This is cold" },
            { left: "Missing item", right: "I didn't get..." }
          ],
          a: "completed",
          successtip: "✓COMPLAINTS!\nBe polite but firm 💪\nRestaurants want to fix problems\nMost will replace/refund\nDon't suffer in silence! 😅"
        },
        {
          type: "mc",
          q: "Polite way to say wrong dish: '___'",
          options: ["Excuse me, this isn't what I ordered", "This is wrong!", "You messed up!"],
          a: "Excuse me, this isn't what I ordered",
          tip: "Be polite even when complaining!",
          successtip: "✓POLITE COMPLAINTS!\nExcuse me = attention getter ✋\nI think... = soften it\nThis isn't... = state problem\nNever yell! ❌"
        },
        {
          type: "order",
          q: "Context: Falta algo de tu orden. Traduce: No recibí mis papas fritas.",
          words: ["I", "didn't", "get", "my", "fries."],
          a: "I didn't get my fries.",
          tip: "DIDN'T GET = no recibí",
          successtip: "✓MISSING ITEMS!\n'I didn't get...' = faltó algo 🍟\nCommon con take-out\nAlways check bag antes de salir!"
        },
        {
          type: "input",
          q: "Food arrived not hot enough: 'This is ___.'",
          a: "cold",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓COLD FOOD!\n'This is cold' = problema común ❄️\nThey'll reheat or replace\nTotally acceptable complaint\nDon't eat cold food! 🚫"
        },
        {
          type: "mc",
          q: "Meat cooked too much (you wanted medium):",
          options: ["This is overcooked", "This is undercooked", "This is perfect"],
          a: "This is overcooked",
          tip: "OVERCOOKED vs UNDERCOOKED",
          successtip: "✓DONENESS!\nOvercooked = demasiado cocido 🔥\nUndercooked = crudo ❄️\nBurgers MUST be cooked (E. coli risk!)\nSteaks = can be rare 🥩"
        },
        {
          type: "order",
          q: "Context: Pidiendo reemplazo cortés. Traduce: ¿Podría traerme otro?",
          words: ["Could", "you", "bring", "me", "another", "one?"],
          a: "Could you bring me another one?",
          tip: "COULD YOU = polite request",
          successtip: "✓REPLACEMENT!\n'Could you bring...' = replacement request 🔄\nMost restaurants happy to fix\nDon't pay for mistakes!"
        },
        {
          type: "mc",
          q: "Asking for condiment: 'Could I get some ___?' (red sauce)",
          options: ["ketchup", "catsup", "both OK!"],
          a: "both OK!",
          successtip: "✓KETCHUP!\n🍅 Ketchup/Catsup = same!\nAmerican staple\nWith fries always\nHeinz = dominant brand\n\n🎮 Fun: Ketchup on eggs? Americans divide! 😂"
        },
        {
          type: "input",
          q: "Fork fell on floor: 'Could I get a ___ fork?'",
          a: "new",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["clean", "another"] },
          successtip: "✓UTENSILS!\nFork, knife, spoon = cubiertos 🍴\nClean one = no problem\nAlso: napkin, plate, glass\nWaiters expect requests!"
        },
        {
          type: "mc",
          q: "Found hair in food (gross!): Best response:",
          options: ["Politely tell waiter, ask for replacement", "Eat around it", "Scream at everyone"],
          a: "Politely tell waiter, ask for replacement",
          successtip: "✓HYGIENE ISSUES!\nHair/bug = immediate replacement 🪰\nMost restaurants comp (free) dish\nManagers get involved\nTotally valid complaint ✓"
        },
        {
          type: "match",
          q: "Match problems to solutions",
          pairs: [
            { left: "Wrong order", right: "They replace it" },
            { left: "Cold food", right: "They reheat/remake it" },
            { left: "Bad quality", right: "You don't pay for it" }
          ],
          a: "completed",
          successtip: "✓CUSTOMER RIGHTS!\nYou pay = you deserve good food ✓\nDon't be shy about problems\nBut always be polite 😊\nTip based on correction quality 💰"
        }
      ]
    },

    // ===================================================================
    // SET 9: PAYING & RESTAURANT CULTURE (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s9",
      title: "Set 9: Payment & Culture",
      questions: [
        {
          type: "match",
          q: "Match payment vocabulary",
          pairs: [
            { left: "Check/Bill", right: "La cuenta 💳" },
            { left: "Split", right: "Dividir 🔪" },
            { left: "Tip/Gratuity", right: "Propina 💵" }
          ],
          a: "completed",
          successtip: "✓GRATUITY!\nGratuity = tip (formal term) 💰\nSometimes added automatically\nLarge groups = often included\nCheck bill carefully! 👀"
        },
        {
          type: "mc",
          q: "Asking for the bill: '___'",
          options: ["Can I get the check, please?", "Money now!", "Pay time!"],
          a: "Can I get the check, please?",
          tip: "CHECK (US) / BILL (UK)",
          successtip: "✓CHECK!\n🇺🇸 'Check, please!'\n🇬🇧 'Bill, please!'\n🇦🇺 'Bill, thanks!'\nWave hand subtly = universal signal 👋"
        },
        {
          type: "order",
          q: "Context: Dividiendo cuenta con amigos. Traduce: ¿Podemos dividir la cuenta?",
          words: ["Can", "we", "split", "the", "check?"],
          a: "Can we split the check?",
          tip: "SPLIT THE CHECK = dividir la cuenta",
          successtip: "✓SPLIT!\nSplit the check = dividir cuenta 🔪\nCommon con amigos\n'Separate checks' = cuentas separadas\nVenmo/PayPal = modern solution 📱"
        },
        {
          type: "input",
          q: "US tipping standard: ___% for good service",
          a: "20",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["15", "15-20", "18"] },
          successtip: "✓TIPPING CULTURE!\n🇺🇸 US: 15-20% MANDATORY 💸\n20% = good service\n15% = OK service\n25%+ = excellent service\n\nWaiters earn $2-3/hr + tips\nThey NEED tips to live!\nNo tip = extremely rude ❌"
        },
        {
          type: "mc",
          q: "Bill says 'Gratuity included' - you should:",
          options: ["Not add extra tip (already included)", "Add 20% more", "Complain"],
          a: "Not add extra tip (already included)",
          tip: "GRATUITY INCLUDED = tip already added",
          successtip: "✓AUTO-GRATUITY!\nGratuity included = tip already there ✓\nLarge groups (6+) = often auto-added\nCheck carefully!\nDouble-tipping = waste money 💸"
        },
        {
          type: "order",
          q: "Context: Preguntando método de pago. Traduce: ¿Aceptan tarjetas?",
          words: ["Do", "you", "take", "cards?"],
          a: "Do you take cards?",
          tip: "TAKE = accept (payment)",
          successtip: "✓PAYMENT METHODS!\nMost places take cards 💳\nCash less common now\nContactless = tap to pay 📱\nVenmo/PayPal = modern"
        },
        {
          type: "mc",
          q: "Taking food home:",
          options: ["Can I get a to-go box?", "Box me!", "Wrap it!"],
          a: "Can I get a to-go box?",
          successtip: "✓TO-GO BOX!\n🇺🇸 'To-go box' / 'Doggy bag'\n🇬🇧 'Takeaway box'\nLeftovers = normal! 🥡\nAmerican portions = huge\nNo shame in taking home! 👍"
        },
        {
          type: "input",
          q: "Meal for ONE person at mid-range US restaurant: $___-20 (typical)",
          a: "15",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["10", "12"] },
          successtip: "✓PRICES!\nCasual dining: $10-20 per person 💵\nFast food: $8-12\nFine dining: $50+ per person\n+ Tax (varies by state)\n+ Tip (20%)\nReal cost = menu × 1.30 💰"
        },
        {
          type: "mc",
          q: "Fast food vs sit-down restaurant difference:",
          options: ["Fast food = order at counter, no waiter, no tip", "Fast food = more expensive", "No difference"],
          a: "Fast food = order at counter, no waiter, no tip",
          successtip: "✓RESTAURANT TYPES!\n🍔 Fast food: No waiter, no tip, cheap\n🍽️ Casual: Waiter, tip 15-20%, medium price\n🍷 Fine dining: Formal, tip 20%, expensive\n\nTaco Bell ≠ fancy restaurant 😂"
        },
        {
          type: "match",
          q: "Match dining cultures",
          pairs: [
            { left: "US tipping", right: "15-20% mandatory" },
            { left: "UK tipping", right: "10% optional" },
            { left: "Australia tipping", right: "Not expected" }
          ],
          a: "completed",
          successtip: "✓GLOBAL TIPPING!\n🇺🇸 US = MUST tip\n🇬🇧 UK = optional\n🇦🇺 Australia = rare\n🇯🇵 Japan = insulting!\n\nWhen traveling: research tipping culture! 🌍"
        }
      ]
    },

    // ===================================================================
    // SET 10: FOOD EXPERIENCES & RECOMMENDATIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u10-s10",
      title: "Set 10: Food Talk Mastery",
      questions: [
        {
          type: "match",
          q: "Match food phrasal verbs",
          pairs: [
            { left: "Eat out", right: "Eat at restaurant 🍽️" },
            { left: "Order in", right: "Get delivery 🚗" },
            { left: "Pick up", right: "Get take-out 🥡" }
          ],
          a: "completed",
          successtip: "✓EAT OUT!\nEat out = comer fuera 🍽️\nOrder in = pedir delivery 📱\nPick up = recoger take-out 🚗\nTry out = probar restaurante nuevo 🆕"
        },
        {
          type: "mc",
          q: "Have you ever tried Ethiopian food?' (asking experience)",
          options: ["Present Perfect (experience question)", "Past Simple", "Future"],
          a: "Present Perfect (experience question)",
          tip: "HAVE YOU EVER = experience question (Unit 8!)",
          successtip: "✓FOOD EXPERIENCES!\n'Have you ever tried...' = food experience question 🍴\nConnects Unit 8 + Unit 10!\nAsking about new cuisines = common conversation"
        },
        {
          type: "order",
          q: "Context: Recomendando restaurante. Traduce: Deberías probar ese restaurante nuevo.",
          words: ["You", "should", "try", "that", "new", "restaurant."],
          a: "You should try that new restaurant.",
          tip: "SHOULD = advice/recommendation (Unit 5!)",
          successtip: "✓RECOMMENDATIONS!\n'You should try...' = recommendation 👍\nConnects Unit 5!\nFood recommendations = constant conversations\n\n🎯 Piece of cake = easy (idiom!)"
        },
        {
          type: "input",
          q: "'I've ___ there before.' (visited restaurant previously)",
          a: "been",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓BEEN!\n'Been to' = visited (Unit 8!) ✓\nI've been to that restaurant = experience\nGone = still there ❌\nBEEN for restaurants! 🍽️"
        },
        {
          type: "mc",
          q: "When did you last eat there?' (asking specific time)",
          options: ["Past Simple (specific detail)", "Present Perfect", "Future"],
          a: "Past Simple (specific detail)",
          tip: "WHEN = Past Simple (Unit 8 pattern!)",
          successtip: "✓CONVERSATION FLOW!\nHave you been there? (Perfect) 🎯\n↓\nWhen did you go? (Simple) 📅\n\nSame pattern as travel conversations! 💯"
        },
        {
          type: "order",
          q: "Context: Comida increíble. Traduce: ¡La comida estaba increíble!",
          words: ["The", "food", "was", "incredible!"],
          a: "The food was incredible!",
          tip: "WAS = past description",
          successtip: "✓REVIEWS!\nThe food was... = review pattern 📝\nIncredible/amazing/delicious/awful\nPast Simple for specific experience\n\n🎮 Spill the beans = reveal secret (idiom!)"
        },
        {
          type: "mc",
          q: "Modern food ordering: 'I ___ food on UberEats.' (ordered)",
          options: ["ordered", "made", "cooked"],
          a: "ordered",
          successtip: "✓DELIVERY APPS!\nUberEats, DoorDash, Grubhub 📱\n'Order on' + app name\nModern dining revolution\nNo tip shame = digital tips 😅\nMore expensive but convenient!"
        },
        {
          type: "input",
          q: "Describing must-visit: 'This place is a ___-try!' (must)",
          a: "must",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓MUST-TRY!\nMust-try = debes probar ⭐\nRestaurant recommendation phrase\n'Hidden gem' = secret good place 💎\n\n🥔 Couch potato = lazy person (idiom!)"
        },
        {
          type: "mc",
          q: "Natural food convo: 'I've heard this place is amazing.' Next:",
          options: ["'Let's try it out!' / 'Have you been there?'", "'I hate food'", "'Never eat'"],
          a: "'Let's try it out!' / 'Have you been there?'",
          successtip: "✓NATURAL FLOW!\n'I've heard...' = reputation 👂\n'Let's try it out' = suggestion 🎯\n'Have you been?' = asking experience ❓\n\nFood talk = friendship building! 🤝"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Most natural restaurant conversation:",
          options: ["'Have you been to that Italian place?' 'Yes! I went last week. The pasta was amazing!'", "'Did you go Italian place?' 'Yes! I have gone.'", "'Are you going Italian?' 'I went.'"],
          a: "'Have you been to that Italian place?' 'Yes! I went last week. The pasta was amazing!'",
          successtip: "✓UNIT 10 COMPLETE! 🎉🏆\n\nMAESTRÍA CULINARIA:\n✓ WOULD LIKE for ordering\n✓ CAN/COULD for requests\n✓ Present Perfect for experiences\n✓ Past Simple for details\n✓ 100+ FOOD VOCABULARY:\n  🍽️ Restaurant: menu, waiter, booth, reservation\n  🥗 Meals: appetizer, entrée, side, dessert\n  👨‍🍳 Cooking: grill, bake, fry, roast, steam\n  👅 Taste: sweet, salty, spicy, savory, bitter\n  🥩 Texture: crispy, tender, juicy, chewy\n  🚫 Dietary: vegetarian, vegan, allergic, gluten-free\n  💳 Payment: check, split, tip, gratuity\n  📱 Modern: order in, delivery apps, to-go\n✓ Tipping culture mastered\n✓ Complaint handling (polite!)\n✓ Full restaurant experience\n✓ Cultural insights (US/UK/Australia)\n✓ 3 food idioms! 🎯\n\nPuedes ahora:\n🍽️ Order confidently\n📋 Navigate menus\n🗣️ Handle dietary needs\n💬 Describe food naturally\n👨‍🍳 Understand cooking methods\n💸 Pay & tip correctly\n🌍 Navigate food culture\n📱 Use delivery apps\n🎯 Make recommendations\n😊 Complain politely\n\n¡LEVEL B2 CONSOLIDADO!\n¡Eres un foodie fluent! 🌟\n\nMódulo 6: 10/12 units complete! 🚀"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - FOOD BLOG REVIEW (10 preguntas)
  // ===================================================================
  readingSet: {
    id: "m6-u10-reading",
    title: "Reading Practice - Food Blog Review",
    category: "reading",
    
    questions: [
      // BLOQUE 1: Introduction
      {
        type: "passage",
        passage: `**FoodieAdventures Blog**
**Restaurant Review: "Bella Italia"**
*Posted by Sarah M. | January 2026*

I've been to a lot of Italian restaurants in my life, but Bella Italia might be the best I've ever tried. Located in downtown, this cozy spot has been on my must-try list for months. Last Saturday, my husband and I finally made a reservation and checked it out.

**First Impressions:**
The ambiance is perfect – dim lighting, soft Italian music, and the smell of fresh bread hits you the moment you walk in. The host greeted us warmly and seated us in a comfortable booth by the window. Our server, Marco, was friendly and knowledgeable about the menu. When we asked what he'd recommend, he suggested we try the house specialty: homemade pasta.`
      },
      {
        type: "mc",
        q: "Sarah uses 'I've been to a lot of Italian restaurants' - why Present Perfect?",
        options: ["Life experience (total count in her life)", "One specific past time", "Future plan"],
        a: "Life experience (total count in her life)",
        tip: "HAVE BEEN TO = experience counting",
        successtip: "✓PRESENT PERFECT!\nCounting experiences = Perfect ✓\nSame pattern as Unit 8! 🎯"
      },
      {
        type: "mc",
        q: "What does 'must-try list' mean?",
        options: ["Places she wants to visit", "Places she hates", "Places she's been to"],
        a: "Places she wants to visit",
        tip: "MUST-TRY = debe probar"
      },

      // BLOQUE 2: Food Description
      {
        type: "passage",
        passage: `**The Food:**

We started with the bruschetta appetizer – perfectly toasted bread topped with fresh tomatoes, basil, and olive oil. It was a bit salty, but in a good way. The tomatoes tasted incredibly fresh, like they'd just been picked that morning.

For our entrées, I ordered the fettuccine carbonara, and my husband got the grilled salmon with roasted vegetables. My pasta was cooked perfectly al dente (firm but not hard), and the sauce was rich and creamy. I could taste the real parmesan cheese – not the cheap stuff. My husband's salmon was tender and juicy, with a nice crispy skin from the grill. The roasted vegetables (zucchini, bell peppers, and carrots) had a slightly sweet, caramelized flavor.

The portions were generous – very American-sized! I couldn't finish mine and had to get a to-go box.`
      },
      {
        type: "mc",
        q: "What cooking method for the salmon?",
        options: ["Grilled (cooked on grill)", "Fried (cooked in oil)", "Boiled (cooked in water)"],
        a: "Grilled (cooked on grill)",
        tip: "GRILLED = a la parrilla"
      },
      {
        type: "mc",
        q: "Sarah describes the pasta sauce as 'rich and creamy.' This means:",
        options: ["Heavy with cream/butter", "Light and healthy", "Watery"],
        a: "Heavy with cream/butter",
        successtip: "✓RICH!\nRich = pesado/cremoso 🧈\nDelicious but heavy!"
      },

      // BLOQUE 3: Service & Issues
      {
        type: "passage",
        passage: `**Service:**

Marco was attentive without being annoying. He checked on us twice during the meal with the classic "How is everything?" and made sure our water glasses were always full. When my husband mentioned his salmon was a bit overcooked (he'd ordered medium but it came medium-well), Marco apologized immediately and offered to have the chef make a new one. My husband said it was fine – still tasty – but I appreciated that they took it seriously.

One small issue: we ordered a side of fries but they forgot to bring them. When we reminded Marco, he apologized and brought them right away – and didn't charge us for them. That's good customer service!`
      },
      {
        type: "mc",
        q: "The salmon issue was:",
        options: ["Overcooked (cooked too much)", "Undercooked (too raw)", "Perfect"],
        a: "Overcooked (cooked too much)",
        tip: "OVERCOOKED vs ordered temperature"
      },
      {
        type: "mc",
        q: "When they forgot the fries, the restaurant:",
        options: ["Brought them free (didn't charge)", "Refused to bring them", "Charged extra"],
        a: "Brought them free (didn't charge)",
        successtip: "✓CUSTOMER SERVICE!\nMistakes → free item = good service 👍\nThis is how good restaurants operate!"
      },

      // BLOQUE 4: Dessert & Payment
      {
        type: "passage",
        passage: `**Dessert:**

We were too full, but Marco insisted we at least try the tiramisu – their specialty. I'm so glad he did! It was the best tiramisu I've ever had. Light and not too sweet, with just the right amount of coffee flavor. We ended up sharing one portion, which was plenty for two people.

**The Bill:**

When we asked for the check, I was pleasantly surprised. For two appetizers, two entrées, one dessert, and two glasses of wine, the total was $85 before tip. Very reasonable for the quality! We left a 20% tip ($17) because the service was excellent – Marco definitely earned it.`
      },
      {
        type: "mc",
        q: "Why does Sarah use 'I've ever had' for tiramisu?",
        options: ["Comparing to all tiramisu in her life (experience)", "Talking about one specific time", "Future plan"],
        a: "Comparing to all tiramisu in her life (experience)",
        successtip: "✓SUPERLATIVES!\n'Best I've ever...' = life experience comparison 🏆\nPresent Perfect pattern!"
      },
      {
        type: "mc",
        q: "They left 20% tip because:",
        options: ["Service was excellent (US standard)", "It was required by law", "They felt bad"],
        a: "Service was excellent (US standard)",
        tip: "20% = good service standard in US"
      },

      // BLOQUE 5: Final Verdict
      {
        type: "passage",
        passage: `**Final Verdict:**

⭐⭐⭐⭐⭐ (5 out of 5 stars)

I'd give Bella Italia my highest recommendation. The food is delicious, the service is top-notch, and the prices are fair. I've already made another reservation for next month – I want to try their pizza, which I've heard is incredible.

If you're looking for authentic Italian food and haven't been to Bella Italia yet, you're missing out. This place is definitely a must-try!

**Tips for visiting:**
- Make a reservation (they're always busy on weekends)
- Save room for dessert (the tiramisu is worth it)
- Ask Marco for recommendations – he knows his stuff
- Bring cash if you want to split the check (their system only does one card)`
      },
      {
        type: "mc",
        q: "Sarah says 'I've heard is incredible' - why Present Perfect?",
        options: ["Information from past that's relevant now", "She heard yesterday specifically", "Future information"],
        a: "Information from past that's relevant now",
        successtip: "✓I'VE HEARD!\n'I've heard...' = reputation/info you have now 👂\nCommon Present Perfect usage!"
      },
      {
        type: "mc",
        q: "FINAL: This blog review shows:",
        options: ["Complete restaurant experience with food vocabulary", "Only complaints", "Only prices"],
        a: "Complete restaurant experience with food vocabulary",
        successtip: "✓READING COMPLETE! 🎉\n\nVOCABULARY FOUND:\n🍽️ Restaurant: reservation, booth, host, server, check\n🥗 Menu: appetizer, entrée, side dish, dessert\n👨‍🍳 Cooking: grilled, roasted, toasted\n👅 Taste: salty, sweet, fresh, rich, creamy\n🥩 Texture: crispy, tender, juicy, firm\n💳 Payment: bill, tip, split check\n📝 Reviews: must-try, recommendation, specialty\n\nGRAMMAR PATTERNS:\n✓ Present Perfect: I've been, I've heard, I've ever had\n✓ Past Simple: We ordered, It was, Marco brought\n✓ Cooking methods: grilled, roasted, toasted\n✓ Comparisons: best I've ever tried\n\nRESTAURANT EXPERIENCE:\n✅ Making reservation\n✅ Being seated\n✅ Ordering (appetizers, entrées, dessert)\n✅ Describing food (taste & texture)\n✅ Handling issues (overcooked, forgot item)\n✅ Good service response\n✅ Paying & tipping (20%)\n✅ Making recommendation\n\nCULTURAL NOTES:\n💡 American portions = huge\n💡 To-go boxes = normal\n💡 20% tip = excellent service\n💡 Servers check multiple times\n💡 Mistakes = free items\n\n¡Authentic restaurant review understood! 🌟"
      }
    ]
  },

   // ===================================================================
  // REVIEW SET (10 preguntas) - CONTINUACIÓN
  // ===================================================================
  reviewSet: {
    id: "m6-u10-review",
    title: "Unit Review - Food Mastery",
    category: "review",
    exercises: [
      {
        type: "mc",
        q: "REVIEW: Most polite way to order:",
        options: ["I'd like the pasta, please", "I want pasta", "Give me pasta"],
        a: "I'd like the pasta, please",
        tip: "I'D LIKE = most polite"
      },
      {
        type: "mc",
        q: "REVIEW: Menu sections in order:",
        options: ["Appetizers → Entrées → Desserts", "Desserts → Entrées → Appetizers", "Entrées → Appetizers → Desserts"],
        a: "Appetizers → Entrées → Desserts"
      },
      {
        type: "input",
        q: "REVIEW: Cooked on grill with fire:",
        a: "grilled",
        accept: { ignoreCase: true, ignorePunctuation: true },
        tip: "GRILL = parrilla"
      },
      {
        type: "mc",
        q: "REVIEW: US tipping for good service:",
        options: ["20%", "5%", "0% (not expected)"],
        a: "20%",
        tip: "US = 15-20% mandatory"
      },
      {
        type: "mc",
        q: "REVIEW: No animal products:",
        options: ["vegan", "vegetarian", "pescatarian"],
        a: "vegan",
        tip: "Vegan = 100% plant-based"
      },
      {
        type: "order",
        q: "REVIEW: Traduce - ¿Puedo ver el menú?",
        words: ["Can", "I", "see", "the", "menu?"],
        a: "Can I see the menu?"
      },
      {
        type: "mc",
        q: "REVIEW: Asking about ingredients:",
        options: ["What's in this?", "What's this in?", "Where's this?"],
        a: "What's in this?",
        tip: "WHAT'S IN = qué tiene"
      },
      {
        type: "match",
        q: "REVIEW: Match taste words",
        pairs: [
          { left: "Sweet", right: "Like sugar 🍭" },
          { left: "Spicy", right: "Like peppers 🌶️" },
          { left: "Salty", right: "Like salt 🧂" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "REVIEW: Dividing bill with friends:",
        options: ["Can we split the check?", "Can we break the check?", "Can we cut the check?"],
        a: "Can we split the check?",
        tip: "SPLIT = dividir"
      },
      {
        type: "mc",
        q: "FINAL REVIEW: Complete restaurant conversation:",
        options: ["'I'd like the burger.' 'How would you like that cooked?' 'Medium, please.'", "'I want burger.' 'How you want?' 'Medium.'", "'Give burger.' 'Cook how?' 'Medium.'"],
        a: "'I'd like the burger.' 'How would you like that cooked?' 'Medium, please.'",
        successtip: "✓UNIT 10 REVIEW COMPLETE! 🎉🏆\n\nDOMINIO TOTAL:\n✅ Restaurant vocabulary\n✅ Ordering politely (I'd like)\n✅ Menu navigation\n✅ Dietary restrictions\n✅ Cooking methods\n✅ Taste & texture words\n✅ Complaint handling\n✅ Tipping culture\n✅ Payment vocabulary\n✅ Food experiences\n✅ 100+ food words\n✅ Cultural knowledge\n\n🍽️ ¡FOODIE FLUENT!\n💯 B2 Level consolidated!\n🎯 Real-world restaurant ready!\n\nMódulo 6: 10/12 complete! 🚀\n\n¿Lista para Unit 11? 🌟"
      }
    ]
  }
};
